import { supabase } from '../lib/supabase';

export const applicationService = {
  /**
   * Submit a new job application
   */
  async submitApplication(jobId, formData, skillsData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert skills array to JSONB format
      const skillsAssessment = skillsData?.map(skill => ({
        name: skill?.name,
        required: skill?.required,
        rating: skill?.rating
      }));

      // Prepare application data (convert camelCase to snake_case)
      const applicationData = {
        job_id: jobId,
        applicant_id: user?.id,
        full_name: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone,
        location: formData?.location,
        linkedin: formData?.linkedin || null,
        portfolio: formData?.portfolio || null,
        experience: formData?.experience,
        current_ctc: formData?.currentCtc || null,
        expected_ctc: formData?.expectedCtc,
        notice_period: formData?.noticePeriod,
        willing_to_relocate: formData?.willingToRelocate,
        open_to_remote: formData?.openToRemote,
        why_interested: formData?.whyInterested,
        relevant_skills: formData?.relevantSkills || null,
        skills_assessment: skillsAssessment
      };

      const { data, error } = await supabase?.from('job_applications')?.insert(applicationData)?.select()?.single();

      if (error) throw error;

      // Fetch job details for email notification
      const { data: jobData } = await supabase
        ?.from('jobs')
        ?.select('job_title, user_profiles!employer_id(company_name)')
        ?.eq('id', jobId)
        ?.single();

      // Send confirmation email via Edge Function (non-blocking)
      if (jobData) {
        try {
          const { error: emailError } = await supabase?.functions?.invoke('send-application-confirmation', {
            body: {
              applicantEmail: formData?.email,
              applicantName: formData?.fullName,
              jobTitle: jobData?.job_title,
              companyName: jobData?.user_profiles?.company_name || 'the company'
            }
          });

          if (emailError) {
            console.error('Email sending failed:', emailError?.message);
            // Don't throw error - application was successful even if email fails
          }
        } catch (emailError) {
          console.error('Email sending failed:', emailError?.message);
          // Don't throw error - application was successful even if email fails
        }
      }

      // Convert snake_case to camelCase for React
      return {
        id: data?.id,
        jobId: data?.job_id,
        applicantId: data?.applicant_id,
        fullName: data?.full_name,
        email: data?.email,
        phone: data?.phone,
        location: data?.location,
        linkedin: data?.linkedin,
        portfolio: data?.portfolio,
        experience: data?.experience,
        currentCtc: data?.current_ctc,
        expectedCtc: data?.expected_ctc,
        noticePeriod: data?.notice_period,
        willingToRelocate: data?.willing_to_relocate,
        openToRemote: data?.open_to_remote,
        whyInterested: data?.why_interested,
        relevantSkills: data?.relevant_skills,
        skillsAssessment: data?.skills_assessment,
        status: data?.status,
        appliedAt: data?.applied_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error submitting application:', error);
      throw error;
    }
  },

  /**
   * Get all applications for current user (job seeker)
   */
  async getUserApplications() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('job_applications')?.select(`
          *,
          jobs:job_id (
            id,
            job_title,
            city,
            state,
            employment_type,
            work_mode,
            user_profiles:employer_id (
              company_name
            )
          )
        `)?.eq('applicant_id', user?.id)?.order('applied_at', { ascending: false });

      if (error) throw error;

      return data?.map(app => ({
        id: app?.id,
        jobId: app?.job_id,
        fullName: app?.full_name,
        email: app?.email,
        status: app?.status,
        appliedAt: app?.applied_at,
        job: {
          id: app?.jobs?.id,
          title: app?.jobs?.job_title,
          location: `${app?.jobs?.city}, ${app?.jobs?.state}`,
          type: app?.jobs?.employment_type,
          workMode: app?.jobs?.work_mode,
          company: app?.jobs?.user_profiles?.company_name
        }
      }));
    } catch (error) {
      console.error('Error fetching user applications:', error);
      throw error;
    }
  },

  /**
   * Get applications for a specific job (employer view)
   */
  async getJobApplications(jobId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('job_applications')?.select(`
          *,
          user_profiles:applicant_id (
            full_name,
            email,
            phone,
            location
          )
        `)?.eq('job_id', jobId)?.order('applied_at', { ascending: false });

      if (error) throw error;

      return data?.map(app => ({
        id: app?.id,
        applicantId: app?.applicant_id,
        fullName: app?.full_name,
        email: app?.email,
        phone: app?.phone,
        location: app?.location,
        experience: app?.experience,
        expectedCtc: app?.expected_ctc,
        noticePeriod: app?.notice_period,
        status: app?.status,
        appliedAt: app?.applied_at,
        skillsAssessment: app?.skills_assessment
      }));
    } catch (error) {
      console.error('Error fetching job applications:', error);
      throw error;
    }
  }
};